// src/components/features/VehicleRecalls/VehicleRecallStatusSelector/VehicleRecallStatusSelector.tsx
//
// Chip-based single-select for vehicle recall status (Active / Dismissed / Resolved).
// Uses Mantine Chip.Group for a compact, touch-friendly UI.
// Supports clearable selection (clicking the active chip deselects it).
//
// Styling matches ExpenseScheduleStatusSelector pattern.

import React, { useMemo, useCallback } from "react";
import { Chip, Input, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { VEHICLE_RECALL_STATUS, VehicleRecallStatusValue } from "@/utils/api";
import { AppIcon } from "@/components/generic";

// ============================================
// Types
// ============================================

export type { VehicleRecallStatusValue };

export interface VehicleRecallStatusSelectorProps {
  /** Currently selected value (null = all / none selected) */
  value: VehicleRecallStatusValue | null;

  /** Callback when selection changes */
  onChange: (value: VehicleRecallStatusValue | null) => void;

  /** Optional label displayed above the chips */
  label?: string;

  /** Whether the field is required (disables deselect) */
  required?: boolean;

  /** Error message to display */
  error?: string | null | undefined;

  /** Disable interaction */
  disabled?: boolean;

  /** Size of the input wrapper */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Chip variant */
  variant?: "outline" | "filled" | "light";
}

// ============================================
// Chip Config
// ============================================

interface ChipConfig {
  value: VehicleRecallStatusValue;
  labelKey: string;
  iconName: string;
  color: string;
}

const CHIP_OPTIONS: ChipConfig[] = [
  {
    value: VEHICLE_RECALL_STATUS.ACTIVE,
    labelKey: "vehicleRecalls:status.active",
    iconName: "AlertTriangle",
    color: "red",
  },
  {
    value: VEHICLE_RECALL_STATUS.DISMISSED,
    labelKey: "vehicleRecalls:status.dismissed",
    iconName: "EyeOff",
    color: "gray",
  },
  {
    value: VEHICLE_RECALL_STATUS.RESOLVED,
    labelKey: "vehicleRecalls:status.resolved",
    iconName: "Check",
    color: "green",
  },
];

// ============================================
// Styles
// ============================================

const CHIP_LABEL_STYLES = {
  borderRadius: "var(--mantine-radius-md)",
  height: "42px",
  paddingLeft: "12px",
  paddingRight: "14px",
  fontSize: "var(--mantine-font-size-sm)",
  width: "100%",
  justifyContent: "center",
};

const CHIP_ICON_WRAPPER_STYLES = {
  width: "auto",
  height: "20px",
  paddingRight: "4px",
};

const CHIP_ROOT_STYLES = {
  flex: "1 1 0",
  minWidth: 0,
};

const GROUP_STYLES: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "var(--mantine-spacing-xs)",
};

// ============================================
// Component
// ============================================

const VehicleRecallStatusSelectorComponent: React.FC<VehicleRecallStatusSelectorProps> = ({
  value,
  onChange,
  label,
  required = false,
  error,
  disabled = false,
  size = "md",
  variant = "outline",
}) => {
  const { t } = useTranslation(["vehicleRecalls"]);

  const defaultLabel = label ?? t("vehicleRecalls:filters.status", { defaultValue: "Status" });

  const handleChange = useCallback(
    (selected: string) => {
      if (value !== null && selected === String(value) && !required) {
        onChange(null);
      } else {
        onChange(selected ? (Number(selected) as VehicleRecallStatusValue) : null);
      }
    },
    [value, required, onChange]
  );

  const chips = useMemo(() => {
    return CHIP_OPTIONS.map((option) => (
      <Chip
        key={option.value}
        value={String(option.value)}
        color={option.color}
        variant={variant}
        size="sm"
        disabled={disabled}
        icon={<AppIcon iconName={option.iconName} size={20} />}
        styles={{
          label: CHIP_LABEL_STYLES,
          iconWrapper: CHIP_ICON_WRAPPER_STYLES,
          root: CHIP_ROOT_STYLES,
        }}
      >
        {t(option.labelKey)}
      </Chip>
    ));
  }, [t, variant, disabled]);

  return (
    <Input.Wrapper
      label={defaultLabel}
      required={required}
      error={error}
      size={size}
    >
      <Box mt={4} style={GROUP_STYLES}>
        <Chip.Group
          multiple={false}
          value={value !== null ? String(value) : ""}
          onChange={handleChange}
        >
          {chips}
        </Chip.Group>
      </Box>
    </Input.Wrapper>
  );
};

export const VehicleRecallStatusSelector = React.memo(VehicleRecallStatusSelectorComponent);